/* ═══════════════════════════════════════════════════════════════════
   colorThemeEngine.js — Dynamic CSS-variable-based color theming
   ═══════════════════════════════════════════════════════════════════
   Converts a single primary hex color into a full set of CSS custom
   properties that every template can consume, regardless of which
   variable names it uses internally.

   Architecture:
     1. Primary → derive light / bg / dark shades via HSL math
     2. Build a :root block covering ALL variable aliases used by
        every template in silver / gold / platinum tiers
     3. Inject as a <style id="rg-color-theme"> block before </head>
     4. For real-time updates, post a message to the iframe so it
        can patch the existing <style> without a full reload
   ═══════════════════════════════════════════════════════════════════ */

// ── Tier constants ──────────────────────────────────────────────
export const TIERS = { SILVER: 'silver', GOLD: 'gold', PLATINUM: 'platinum' };

// ── Default palette definitions ─────────────────────────────────
// Each entry: { primary, twFamily } — twFamily maps to a Tailwind color
// family name so utility classes can be swapped (e.g. bg-blue-500 → bg-purple-500).
export const COLOR_PALETTES = {
  teal:    { primary: '#0d9488', twFamily: 'teal' },
  blue:    { primary: '#2563eb', twFamily: 'blue' },
  purple:  { primary: '#9333ea', twFamily: 'purple' },
  indigo:  { primary: '#4f46e5', twFamily: 'indigo' },
  rose:    { primary: '#e11d48', twFamily: 'rose' },
  emerald: { primary: '#059669', twFamily: 'emerald' },
  cyan:    { primary: '#06b6d4', twFamily: 'cyan' },
  amber:   { primary: '#f59e0b', twFamily: 'amber' },
  orange:  { primary: '#f97316', twFamily: 'orange' },
  pink:    { primary: '#ec4899', twFamily: 'pink' },
  red:     { primary: '#dc2626', twFamily: 'red' },
  green:   { primary: '#16a34a', twFamily: 'green' },
  slate:   { primary: '#475569', twFamily: 'slate' },
  charcoal:{ primary: '#1e293b', twFamily: 'slate' },
};

// All Tailwind color families recognized by the swapper. Order matters only
// for the regex alternation — broader matches first is fine.
const TW_COLOR_FAMILIES = [
  'slate','gray','zinc','neutral','stone',
  'red','orange','amber','yellow','lime','green','emerald','teal','cyan',
  'sky','blue','indigo','violet','purple','fuchsia','pink','rose'
];

// Tailwind utility prefixes whose color suffix should be swapped together.
const TW_UTIL_PREFIXES = [
  'bg','text','border','from','to','via','ring','fill','stroke',
  'decoration','divide','outline','placeholder','caret','accent','shadow'
];

/* ─────────────────────────────────────────────────────────────────
   Color math helpers (pure, zero-dependency)
   ───────────────────────────────────────────────────────────────── */

/** Parse "#rrggbb" or "#rgb" → [r, g, b] each 0-255 */
function hexToRgb(hex) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** [r,g,b] → "#rrggbb" */
function rgbToHex([r, g, b]) {
  return '#' + [r, g, b].map(v => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('');
}

/** [r,g,b] 0-255 → [h,s,l] h:0-360 s/l:0-1 */
function rgbToHsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return [h * 360, s, l];
}

/** [h,s,l] → [r,g,b] */
function hslToRgb([h, s, l]) {
  h /= 360;
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  ];
}

/** Lighten / darken a hex color by `amount` (0-1). Positive = lighter. */
function adjustLightness(hex, amount) {
  const [h, s, l] = rgbToHsl(hexToRgb(hex));
  return rgbToHex(hslToRgb([h, s, Math.min(1, Math.max(0, l + amount))]));
}

/** Create a very light tint (for badge backgrounds — subtle, not for large surfaces). */
function tintBg(hex) {
  const [h, s, l] = rgbToHsl(hexToRgb(hex));
  // For dark/charcoal/achromatic targets, return a neutral slate-tinted gray
  if ((s < 0.1) || (s < 0.4 && l < 0.2)) return '#f1f5f9';
  return rgbToHex(hslToRgb([h, Math.min(s, 0.6), 0.94]));
}

/** Neutral near-white background — safe for large surfaces. */
function neutralBg() {
  return '#f8f9fa';
}

/** Create a lighter shade (for borders / highlights). */
function lighten(hex) {
  return adjustLightness(hex, 0.2);
}

/** Create a darker shade (for dark text / gradients). */
function darken(hex) {
  return adjustLightness(hex, -0.15);
}

/** rgba() string from hex + alpha. */
function hexToRgba(hex, alpha) {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/* ─────────────────────────────────────────────────────────────────
   Derive a full variable set from one primary color
   ───────────────────────────────────────────────────────────────── */

/**
 * Given a primary hex color, compute every CSS variable that any
 * template in the codebase might reference.
 *
 * @param {string} primary — e.g. "#2563eb"
 * @returns {Record<string, string>} map of CSS var name → value
 */
export function deriveThemeVariables(primary) {
  const [, pSat, pLight] = rgbToHsl(hexToRgb(primary));

  // ── Special dark/charcoal/black path ──
  // When the user picks a very dark color (charcoal #1e293b, black, etc.),
  // produce a meaningful monochrome dark palette. Detects both true achromatic
  // (s<0.1) AND dark desaturated colors like charcoal (s<0.4 && l<0.2).
  const isDarkCharcoal = pLight < 0.2 && pSat < 0.4;
  if (isDarkCharcoal) {
    return {
      '--primary-color': '#1e293b',
      '--primary-light': '#475569',
      '--primary-bg':    '#f1f5f9',
      '--primary-dark':  '#0f172a',
      '--gradient-from': '#0f172a',
      '--gradient-to':   '#334155',
      '--accent-color':  '#1e293b',
      '--accent-light':  '#e2e8f0',
      '--accent-bg':     '#f1f5f9',
      '--main-color':    '#0f172a',
      '--main-light':    '#f1f5f9',
      '--main-border':   '#1e293b',
      '--primary':       '#1e293b',
      '--secondary':     '#475569',
      '--light-bg':      '#f1f5f9',
      '--dark-text':     '#0f172a',
      '--gradient-start': '#1e293b',
      '--gradient-end':   '#475569',
      '--hero-start':     '#1e293b',
      '--hero-end':       '#334155',
      '--accent-main':    '#1e293b',
      '--accent-border':  '#475569',
      '--circuit-color':  '#475569',
    };
  }

  const light   = lighten(primary);
  const tint    = tintBg(primary);   // subtle tint for small accent elements only
  const neutral = neutralBg();       // safe neutral for large backgrounds
  const dark    = darken(primary);

  return {
    // ── Standard names (majority of gold + platinum templates) ──
    '--primary-color': primary,
    '--primary-light': light,
    '--primary-bg':    neutral,       // ← neutral, NOT tinted — safe for large surfaces
    '--primary-dark':  dark,

    // ── Gradient variables (standardized API) ──
    '--gradient-from': dark,
    '--gradient-to':   light,

    // ── Aliases used by gold-51 (accent-*) ──
    '--accent-color':  primary,
    '--accent-light':  tint,
    '--accent-bg':     tint,

    // ── Aliases used by gold-52 (main-*) ──
    '--main-color':    dark,
    '--main-light':    neutral,
    '--main-border':   primary,

    // ── Aliases used by platinum-54 (short names) ──
    '--primary':       primary,
    '--secondary':     light,
    '--light-bg':      neutral,
    '--dark-text':     dark,

    // ── Aliases used by platinum-53 / platinum-55 (gradient-*) ──
    '--gradient-start': primary,
    '--gradient-end':   light,
    '--hero-start':     primary,
    '--hero-end':       light,
    '--accent-main':    primary,
    '--accent-border':  light,

    // ── Special uses (platinum-51 circuit theme) ──
    '--circuit-color':  light,
  };
}

/* ─────────────────────────────────────────────────────────────────
   Build injectable CSS block
   ───────────────────────────────────────────────────────────────── */

const STYLE_ID = 'rg-color-theme';

/**
 * Build a `<style>` block that sets all theme variables on :root.
 * Designed to be injected into the template HTML before </head>.
 *
 * Uses `!important` so the injected values override any per-template
 * :root defaults (e.g. `--primary-color: #8b5cf6` in platinum-66).
 */
export function buildThemeStyleBlock(primary) {
  const vars = deriveThemeVariables(primary);
  const declarations = Object.entries(vars)
    .map(([prop, val]) => `    ${prop}: ${val} !important;`)
    .join('\n');

  return `<style id="${STYLE_ID}">\n  :root {\n${declarations}\n  }\n</style>`;
}

/**
 * Inject the theme style block into an HTML string (before </head>).
 * Replaces any previous injection.
 *
 * ALSO remaps hardcoded template colors (hex + Tailwind utility classes)
 * to the new theme — see `remapTemplateColors` below. This is why color
 * changes work even on templates that do NOT use CSS variables.
 *
 * @param {string} html — full template HTML
 * @param {string} primary — hex color e.g. "#2563eb"
 * @returns {string} HTML with theme variables injected AND colors remapped
 */
export function injectThemeIntoHTML(html, primary) {
  if (!html || !primary) return html;

  // Step 1: remap hardcoded colors throughout the document (hex + tailwind).
  // This is the heart of the new theming — works for templates that don't
  // consume CSS variables at all.
  let result = remapTemplateColors(html, primary);

  // Step 2: inject CSS variable :root block (for templates that DO consume
  // var(--primary-color) directly). Cheap and additive.
  const cleaned = result.replace(/<style id="rg-color-theme">[\s\S]*?<\/style>\s*/g, '');
  const block = buildThemeStyleBlock(primary);
  if (/<\/head>/i.test(cleaned)) {
    result = cleaned.replace(/<\/head>/i, `${block}\n</head>`);
  } else {
    result = `${block}\n${cleaned}`;
  }
  return result;
}

/* ─────────────────────────────────────────────────────────────────
   Color remapping engine
   ─────────────────────────────────────────────────────────────────
   Strategy:
     1. Auto-detect the template's dominant "brand color" (most-used
        saturated hex, weighted higher when it appears in inline styles).
     2. Compute the hue shift between the brand color and the target.
     3. Rotate every hex in the same hue family (within ±60° of the brand)
        by that shift — keeping its own saturation & lightness so tints
        stay tints and darks stay darks.
     4. Detect the template's dominant Tailwind color family and swap it
        for the target family across all utility prefixes (bg-, text-, …).

   Skipped automatically:
     - Near-white / near-black (lightness > 0.95 or < 0.05)
     - Grays (saturation < 0.15) → page text stays neutral
     - Colors more than 60° away from the brand hue → keeps unrelated
        accents (chart greens in a blue template, etc.) intact
   ───────────────────────────────────────────────────────────────── */

// Reference hexes for each Tailwind family (the 500 shade) — used to map a
// target primary back to its closest family for class swapping.
const TW_FAMILY_REFS = {
  slate:    '#64748b',  gray:     '#6b7280',  zinc:     '#71717a',
  neutral:  '#737373',  stone:    '#78716c',
  red:      '#ef4444',  orange:   '#f97316',  amber:    '#f59e0b',
  yellow:   '#eab308',  lime:     '#84cc16',  green:    '#22c55e',
  emerald:  '#10b981',  teal:     '#14b8a6',  cyan:     '#06b6d4',
  sky:      '#0ea5e9',  blue:     '#3b82f6',  indigo:   '#6366f1',
  violet:   '#8b5cf6',  purple:   '#a855f7',  fuchsia:  '#d946ef',
  pink:     '#ec4899',  rose:     '#f43f5e'
};

/**
 * Find the closest Tailwind color family name for a given hex.
 * Returns null for grayscale targets (we never want to swap a brand into gray).
 */
function hexToTwFamily(hex) {
  const [h, s] = rgbToHsl(hexToRgb(hex));
  if (s < 0.2) return null;
  let best = null;
  let bestDist = Infinity;
  for (const [family, ref] of Object.entries(TW_FAMILY_REFS)) {
    const [rh, rs] = rgbToHsl(hexToRgb(ref));
    if (rs < 0.2) continue; // skip grayscale families
    const hueDist = Math.min(Math.abs(h - rh), 360 - Math.abs(h - rh));
    if (hueDist < bestDist) { bestDist = hueDist; best = family; }
  }
  return best;
}

/**
 * Detect the most prominent Tailwind color family used in template markup.
 * Counts occurrences of every (prefix)-(family)-(shade) utility, weighted by
 * the saturation of the family (gray counts less). Returns null if no
 * saturated family dominates.
 */
function detectTwBrandFamily(html) {
  const re = new RegExp(
    `\\b(?:${TW_UTIL_PREFIXES.join('|')})-(${TW_COLOR_FAMILIES.join('|')})-\\d+\\b`,
    'g'
  );
  const counts = {};
  let m;
  while ((m = re.exec(html)) !== null) {
    const family = m[1];
    counts[family] = (counts[family] || 0) + 1;
  }
  let best = null;
  let bestScore = 0;
  for (const [family, count] of Object.entries(counts)) {
    const ref = TW_FAMILY_REFS[family];
    if (!ref) continue;
    const [, sat] = rgbToHsl(hexToRgb(ref));
    // Weight saturated families 3× higher than grays
    const score = count * (sat < 0.2 ? 1 : 3);
    if (score > bestScore) { bestScore = score; best = family; }
  }
  return best;
}

/**
 * Detect the dominant hex "brand color" of a template.
 * Inline-style hexes weigh more than <style>-block hexes because they're
 * directly attached to visible elements. Grays/whites/blacks are ignored.
 */
function detectBrandHex(html) {
  const tallies = new Map(); // hex (lowercased) → weighted count

  // Higher weight for hexes inside style="…" attributes
  const inlineStyleRe = /\sstyle\s*=\s*["']([^"']*)["']/gi;
  let m;
  while ((m = inlineStyleRe.exec(html)) !== null) {
    const hexes = m[1].match(/#[0-9a-fA-F]{6}\b/g) || [];
    for (const hex of hexes) tallyHex(tallies, hex.toLowerCase(), 3);
  }

  // Lower weight for any other hex (CSS blocks, attributes, etc.)
  const allHexRe = /#[0-9a-fA-F]{6}\b/g;
  while ((m = allHexRe.exec(html)) !== null) {
    tallyHex(tallies, m[0].toLowerCase(), 1);
  }

  // Pick most-frequent saturated, non-extreme color
  let best = null;
  let bestScore = 0;
  for (const [hex, score] of tallies) {
    const [, s, l] = rgbToHsl(hexToRgb(hex));
    if (s < 0.25) continue;          // skip grays
    if (l < 0.08 || l > 0.92) continue; // skip near-black / near-white
    if (score > bestScore) { bestScore = score; best = hex; }
  }
  return best;
}

function tallyHex(map, hex, weight) {
  map.set(hex, (map.get(hex) || 0) + weight);
}

/**
 * Remap every "brand-family" color in a template to a new primary.
 * Returns the original HTML if no brand color can be confidently detected
 * AND no Tailwind family is present.
 *
 * @param {string} html
 * @param {string} targetHex — the user-picked primary color
 */
export function remapTemplateColors(html, targetHex) {
  if (!html || !targetHex) return html;

  const targetHsl = rgbToHsl(hexToRgb(targetHex));
  const isDarkCharcoal = targetHsl[2] < 0.2 && targetHsl[1] < 0.4;
  const targetTwFamily = hexToTwFamily(targetHex);
  let result = html;

  // ── Hex remapping ─────────────────────────────────────────────
  const brandHex = detectBrandHex(result);
  if (brandHex) {
    const brandHsl = rgbToHsl(hexToRgb(brandHex));
    const hueShift = targetHsl[0] - brandHsl[0];

    result = result.replace(/#([0-9a-fA-F]{6})\b/g, (match) => {
      const [h, s, l] = rgbToHsl(hexToRgb(match));
      // Preserve grays, near-white & near-black
      if (s < 0.15) return match;
      if (l < 0.05 || l > 0.97) return match;
      // Only rotate colors in the brand's hue family
      const hueDist = Math.min(Math.abs(h - brandHsl[0]), 360 - Math.abs(h - brandHsl[0]));
      if (hueDist > 60) return match;

      // ── Special dark/charcoal target: desaturate to dark slate grays ──
      if (isDarkCharcoal) {
        // Convert brand color to a corresponding dark gray (preserving lightness relationship)
        const newL = Math.max(0.05, l * 0.6);  // darken proportionally
        return rgbToHex(hslToRgb([215, 0.15, newL]));  // slight blue-gray tint (slate)
      }

      const newHue = (h + hueShift + 360) % 360;
      // Slightly nudge saturation toward target so the new color reads as the new theme
      const newSat = Math.min(1, s * 0.75 + targetHsl[1] * 0.25);
      return rgbToHex(hslToRgb([newHue, newSat, l]));
    });
  }

  // ── Tailwind utility class remapping ──────────────────────────
  // For dark/charcoal targets, swap brand family to slate (dark neutral)
  const effectiveTwFamily = isDarkCharcoal ? 'slate' : targetTwFamily;
  if (effectiveTwFamily) {
    const brandTwFamily = detectTwBrandFamily(result);
    if (brandTwFamily && brandTwFamily !== effectiveTwFamily) {
      const swapRe = new RegExp(
        `\\b((?:${TW_UTIL_PREFIXES.join('|')}))-${brandTwFamily}(-\\d+)\\b`,
        'g'
      );
      result = result.replace(swapRe, `$1-${effectiveTwFamily}$2`);
    }
  }

  return result;
}

/* ─────────────────────────────────────────────────────────────────
   Real-time update script (runs inside iframe)
   ───────────────────────────────────────────────────────────────── */

/**
 * Returns a <script> block to be injected into the iframe once.
 * It listens for `rg-theme-update` postMessages and instantly
 * patches the :root CSS variables — no re-render, no flicker.
 */
export function buildThemeListenerScript() {
  return `
<script id="rg-theme-listener">
(function(){
  window.addEventListener('message', function(e) {
    if (!e.data || e.data.type !== 'rg-theme-update') return;
    var vars = e.data.variables;
    if (!vars) return;
    var root = document.documentElement;
    for (var key in vars) {
      if (vars.hasOwnProperty(key)) {
        root.style.setProperty(key, vars[key], 'important');
      }
    }
  });
})();
</script>`;
}

/* ─────────────────────────────────────────────────────────────────
   SKU-based color rules
   ───────────────────────────────────────────────────────────────── */

/**
 * Determine whether color theming is supported for a given tier.
 *
 * - Silver: color changes are disabled (minimalist black & white)
 * - Gold: moderate accent colors allowed
 * - Platinum: full rich color palette
 *
 * @param {string} tier — "silver" | "gold" | "platinum"
 * @returns {{ enabled: boolean, label: string }}
 */
export function getTierColorPolicy(tier) {
  const t = (tier || '').toLowerCase();
  if (t === 'silver') {
    return { enabled: false, label: 'Color themes are not available for Silver templates' };
  }
  return { enabled: true, label: '' };
}

/**
 * Filter the palette keys available for a given tier.
 * Silver → empty (no colors). Gold → all. Platinum → all.
 */
export function getAvailablePalettes(tier) {
  const policy = getTierColorPolicy(tier);
  if (!policy.enabled) return {};
  return COLOR_PALETTES;
}
